import { useState } from "react";
import { ChevronDown, ChevronUp, Compass } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatCurrencyGbpCompact } from "@shared-core/domain/formatters";
import { Badge } from "../../primitives/badge";
import { IntentCardHeader } from "./IntentCardPrimitives";

interface CustomerStrategyBannerProps {
  strategic: API.CustomerStrategicIntelligenceView | null | undefined;
  /** Total estimated wallet (GBP) — the hero value on the banner. */
  walletGbp: number | null;
  /** Last 12 months revenue (GBP). Unused by the banner today; kept
   *  on the prop set for future headroom/captured calculations. */
  revenue12MGbp: number;
}

/**
 * Account-level strategy banner. Apex of the customer-page pyramid.
 * Layout mirrors the Pulse card: label (with icon) → hero (addressable
 * spend) → headline strategy badge → AI-generated narrative. Narrative
 * is line-clamped to 4 lines with a show-more toggle when it overflows.
 *
 * ``strategy_summary`` is an LLM-authored 2–3 word headline label
 * (e.g. "Build relationship", "Execute growth", "Defensive hold"). Rows
 * predating the field render with no badge; the narrative alone carries
 * the banner.
 */
export function CustomerStrategyBanner({
  strategic,
  walletGbp,
}: CustomerStrategyBannerProps) {
  const [expanded, setExpanded] = useState(false);

  const summary = strategic?.strategy_summary ?? null;
  const narrative = strategic?.overall_opportunity_assessment ?? null;

  // Hide the banner only when genuinely nothing to show.
  const hasAnyContent = summary || narrative || walletGbp != null;
  if (!hasAnyContent) return null;

  // Rough char threshold for "probably wraps past 4 lines". Cheaper
  // than measuring; good enough to decide whether to show the toggle.
  const NARRATIVE_CLAMP_THRESHOLD = 220;
  const narrativeOverflows =
    narrative != null && narrative.length > NARRATIVE_CLAMP_THRESHOLD;

  return (
    <div className="space-y-3">
      <IntentCardHeader
        label={
          <span className="inline-flex items-center gap-1.5">
            <Compass className="size-4" />
            Strategy
          </span>
        }
        hero={
          walletGbp != null ? (
            <span>
              {formatCurrencyGbpCompact(walletGbp)}{" "}
              <span className="text-base text-muted-foreground font-normal">
                addressable spend
              </span>
            </span>
          ) : (
            "—"
          )
        }
        badge={
          summary && (
            <Badge variant="default" className="text-sm">
              {summary}
            </Badge>
          )
        }
        icon={<Compass className="size-5" />}
        tone="primary"
      />

      {narrative && (
        <div className="space-y-1">
          <p
            className={`text-base text-foreground leading-snug ${
              expanded || !narrativeOverflows ? "" : "line-clamp-4"
            }`}
          >
            {narrative}
          </p>
          {narrativeOverflows && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              {expanded ? (
                <>
                  Show less <ChevronUp className="size-4" />
                </>
              ) : (
                <>
                  Show more <ChevronDown className="size-4" />
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
